import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Folder, initialAssetDisplayState} from 'common/src/modules/Assets/Assets.types';
import updateFolderInTree from 'common/src/modules/Assets/store/shared/utils/updateFolderInTree';
import updateAssetInTree from 'common/src/modules/Assets/store/shared/utils/updateAssetInTree';
import removeAssetFromTree from 'common/src/modules/Assets/store/shared/utils/removeAssetFromTree';
import removeFolderFromTree from 'common/src/modules/Assets/store/shared/utils/removeFolderFromTree';
import {AssetDisplayState} from 'common/src/modules/Assets/Assets.types';

const initialState: AssetDisplayState = initialAssetDisplayState;

const templateAssetsSlice = createSlice({
  name: 'templateAssets',
  initialState,
  reducers: {
    clearTemplateAssets: (state) => {
      state.assetTree = null;
      state.currentFolderId = null;
      state.selectedAssetIds = [];
      state.selectedFolderIds = [];
      state.searchTerm = '';
      state.searchResults = null;
      state.isLoadingTree = false;
      state.isLoadingFolderAssets = false;
      state.isSearching = false;
    },
    setAssetTree: (state, action: PayloadAction<Folder | null>) => {
      state.assetTree = action.payload;
    },
    setCurrentFolderId: (state, action: PayloadAction<number | null>) => {
      const previousFolderId = state.currentFolderId;
      state.currentFolderId = action.payload;

      // Clear selection when navigating to a different folder
      if (previousFolderId !== action.payload) {
        state.selectedAssetIds = [];
        state.selectedFolderIds = [];
      }
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSearchType: (state, action: PayloadAction<'name' | 'content'>) => {
      state.searchType = action.payload;
    },
    setSearchResults: (state, action: PayloadAction<Folder | null>) => {
      state.searchResults = action.payload;
    },
    clearSearch: (state) => {
      state.searchTerm = '';
      state.searchResults = null;
      state.isSearching = false;
    },
    setIsLoadingTree: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTree = action.payload;
    },
    setIsLoadingFolderAssets: (state, action: PayloadAction<boolean>) => {
      state.isLoadingFolderAssets = action.payload;
    },
    setIsSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload;
    },
    setSelectedAssetIds: (state, action: PayloadAction<number[]>) => {
      state.selectedAssetIds = action.payload;
    },
    setSelectedFolderIds: (state, action: PayloadAction<number[]>) => {
      state.selectedFolderIds = action.payload;
    },
    clearSelection: (state) => {
      state.selectedAssetIds = [];
      state.selectedFolderIds = [];
    },
    updateFolderName: (state, action: PayloadAction<{folderId: number; newName: string}>) => {
      const {folderId, newName} = action.payload;

      // Update folder name in assetTree
      state.assetTree = updateFolderInTree(state.assetTree, folderId, {folderName: newName});

      // Also update in searchResults if present
      if (state.searchResults) {
        state.searchResults = updateFolderInTree(state.searchResults, folderId, {
          folderName: newName,
        });
      }
    },
    updateAssetName: (state, action: PayloadAction<{assetId: number; newName: string}>) => {
      const {assetId, newName} = action.payload;

      // Update asset name in assetTree
      state.assetTree = updateAssetInTree(state.assetTree, assetId, {name: newName});

      // Also update in searchResults if present
      if (state.searchResults) {
        state.searchResults = updateAssetInTree(state.searchResults, assetId, {name: newName});
      }
    },
    removeAsset: (state, action: PayloadAction<number>) => {
      const assetId = action.payload;

      // Remove asset from assetTree
      state.assetTree = removeAssetFromTree(state.assetTree, assetId);

      // Also remove from searchResults if present
      if (state.searchResults) {
        state.searchResults = removeAssetFromTree(state.searchResults, assetId);
      }

      // Remove from selection if selected
      state.selectedAssetIds = state.selectedAssetIds.filter((id) => id !== assetId);
    },
    removeFolder: (state, action: PayloadAction<number>) => {
      const folderId = action.payload;

      // Remove folder from assetTree
      state.assetTree = removeFolderFromTree(state.assetTree, folderId);

      // Also remove from searchResults if present
      if (state.searchResults) {
        state.searchResults = removeFolderFromTree(state.searchResults, folderId);
      }

      // Remove from selection if selected
      state.selectedFolderIds = state.selectedFolderIds.filter((id) => id !== folderId);
    },
  },
});

export const {
  clearTemplateAssets,
  setAssetTree,
  setCurrentFolderId,
  setSelectedAssetIds,
  setSelectedFolderIds,
  clearSelection,
  setSearchTerm,
  setSearchType,
  setSearchResults,
  clearSearch,
  setIsLoadingTree,
  setIsLoadingFolderAssets,
  setIsSearching,
  updateFolderName,
  updateAssetName,
  removeAsset,
  removeFolder,
} = templateAssetsSlice.actions;

export default templateAssetsSlice.reducer;
